import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../../app/redux/hooks';
import LoadingModal from '../../../../common/components/loading-modal/LoadingModal';
import {
  useCloseWorkstationSessionMutation,
  useEnableWasteModelMutation,
  useGetWorkstationSessionQuery,
  useLazyGetWorkstationsQuery,
  useOperatorSignOffMutation,
  useResendWorkstationSessionMutation,
} from '../../../api';
import { setWorkstation, useOperatorSession } from '../../../appState';
import { useFeatureFlags, useOfflineMode } from '../../../config';
import { showErrorDialog } from '../../../error-dialog/errorDialogSlice';
import { setQuantity, setQuantityShow, useTransaction } from '../../orderSlice';

/** Offline mode only: closing uploads the session to the POS before signing off. */
type UploadState =
  | { stage: 'idle' }
  | { stage: 'closing' }
  | { stage: 'uploading' }
  | { stage: 'uploaded'; message: string; rejected: number }
  | { stage: 'skipped'; message: string }
  | { stage: 'failed'; sessionId?: number; message: string }
  | { stage: 'leaving' };

/** The screen for an upload outcome from the engine (close or Retry); the engine's message is shown as it is. */
const outcomeOf = (
  result: { uploaded?: boolean; skipped?: boolean; message?: string; rejected?: number },
  sessionId?: number,
): UploadState => {
  const message = result.message ?? '';
  if (result.skipped) return { stage: 'skipped', message };
  if (result.uploaded) return { stage: 'uploaded', message, rejected: result.rejected ?? 0 };
  return { stage: 'failed', sessionId, message };
};

function CloseSession() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const transaction = useTransaction();
  const isOfflineMode = useOfflineMode();

  const flags = useFeatureFlags();
  const useWasteMode = flags && flags.useWasteMode === true;

  const [triggerClose] = useCloseWorkstationSessionMutation();
  const [triggerResend] = useResendWorkstationSessionMutation();
  const [triggerSignOff] = useOperatorSignOffMutation();
  const [triggerWorkstation] = useLazyGetWorkstationsQuery();
  const [triggerWaste] = useEnableWasteModelMutation();

  const operatorSession = useOperatorSession();
  const { data: workstationSession } = useGetWorkstationSessionQuery();
  const [upload, setUpload] = useState<UploadState>({ stage: 'idle' });

  const notAllowed = transaction.length > 1 || transaction[0].totalValue > 0;

  // Continue and the auto-continue timer must not sign off twice.
  const leaving = useRef(false);
  // The timer fires once: if sign-off then fails, staff use Continue rather than it retrying every 2 s.
  const autoContinued = useRef(false);

  /** Signs off to Welcome. If that fails, says so and puts back `restore` so Continue (or Close) works again. */
  const signOffAndLeave = useCallback(
    (restore: UploadState, showSpinner = true) => {
      if (operatorSession == null || leaving.current) return;
      leaving.current = true;
      if (showSpinner) setUpload({ stage: 'leaving' });
      const failed = () => {
        leaving.current = false;
        setUpload(restore);
        dispatch(
          showErrorDialog({
            message: 'An error occurred signing out',
            buttonText: 'OK',
            dismissible: true,
          }),
        );
      };
      triggerWorkstation()
        .unwrap()
        .then((ws) => {
          dispatch(setWorkstation(ws));
          triggerSignOff({
            operatorId: operatorSession.operator.id,
          })
            .unwrap()
            .then(() => {
              navigate('/');
            })
            .catch((e) => {
              if (e.status && e.status === 404) navigate('/');
              else failed();
            });
        })
        .catch(failed);
    },
    [dispatch, navigate, operatorSession, triggerSignOff, triggerWorkstation],
  );

  useEffect(() => {
    // Sales the POS didn't accept: staff read the message and press Continue themselves.
    if (upload.stage !== 'uploaded' || upload.rejected > 0 || autoContinued.current) return;
    const shown = upload;
    const timer = setTimeout(() => {
      autoContinued.current = true;
      signOffAndLeave(shown);
    }, 2000);
    return () => clearTimeout(timer);
  }, [upload, signOffAndLeave]);

  const waste = () => {
    if (operatorSession != null) {
      triggerWaste(operatorSession.operator.id)
        .unwrap()
        .then((r) => {
          if (r.wastePermitted === true) navigate('../');
          else {
            dispatch(
              showErrorDialog({
                message: 'Cannot activate waste due to transaction in progress',
                dismissible: true,
                buttonText: 'OK',
              }),
            );
          }
        })
        .catch((err) => {
          if (err && err.status && (err.status === 406 || err.status === 409)) {
            dispatch(
              showErrorDialog({
                message: err.data.detail,
                dismissible: true,
                buttonText: 'OK',
              }),
            );
          } else {
            dispatch(
              showErrorDialog({
                message: 'An error occurred',
                dismissible: true,
                buttonText: 'OK',
              }),
            );
          }
        });
    }
  };

  const close = () => {
    if (operatorSession != null) {
      // Closing prints the session report, then uploads: the label covers both.
      if (isOfflineMode) setUpload({ stage: 'closing' });
      triggerClose(operatorSession.operator.id)
        .unwrap()
        .then((result) => {
          // Online: sign off as before, with no spinner.
          if (!isOfflineMode) signOffAndLeave({ stage: 'idle' }, false);
          // Nothing was open, or an offline engine from before upload-on-close: sign off.
          else if (result == null || result.uploaded === undefined) signOffAndLeave({ stage: 'idle' });
          else setUpload(outcomeOf(result, result.id));
        })
        .catch((err) => {
          setUpload({ stage: 'idle' });
          if (err && err.status && (err.status === 406 || err.status === 409)) {
            dispatch(
              showErrorDialog({
                message: err.data.detail,
                dismissible: true,
                buttonText: 'OK',
              }),
            );
          } else {
            dispatch(
              showErrorDialog({
                message: 'An error occurred',
                dismissible: true,
                buttonText: 'OK',
              }),
            );
          }
        });
    }
  };

  const retry = () => {
    if (upload.stage !== 'failed' || upload.sessionId === undefined) return;
    const sessionId = upload.sessionId;
    setUpload({ stage: 'uploading' });
    triggerResend(sessionId)
      .unwrap()
      .then((result) => setUpload(outcomeOf(result, sessionId)))
      .catch(() => setUpload({ stage: 'failed', sessionId, message: 'The till could not retry the upload' }));
  };

  if (upload.stage === 'closing') return <LoadingModal label="Closing session…" />;
  if (upload.stage === 'uploading') return <LoadingModal label="Uploading to POS…" />;
  if (upload.stage === 'leaving') return <LoadingModal label="Signing off…" />;

  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay"
        onClick={(e) => {
          // While showing an upload outcome the session is already closed: don't drop back to the order screen.
          if (upload.stage !== 'idle') return;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className === 'pos-modal-overlay') {
            dispatch(setQuantityShow(false));
            dispatch(setQuantity(1));
            navigate('/order');
          }
        }}
      >
        <div className="pos-modal pos-modal_role_servers-overview">
          <div className="pos-modal__content">
            <div className="pos-servers-overview">
              <div className="pos-servers-overview__header">
                <h2 className="pos-servers-overview__title">{upload.stage === 'idle' ? 'Confirm Close Session' : 'Session Closed'}</h2>
              </div>
              {upload.stage === 'uploaded' && (
                <>
                  <div className="close-message">
                    <p>{upload.message}</p>
                  </div>
                  <div className="pos-servers-overview__actions">
                    <button className="pos-servers-overview__action" type="button" onClick={() => signOffAndLeave(upload)}>
                      Continue
                    </button>
                  </div>
                </>
              )}
              {upload.stage === 'skipped' && (
                <>
                  <div className="close-message">
                    <p>{upload.message}</p>
                  </div>
                  <div className="pos-servers-overview__actions">
                    <button className="pos-servers-overview__action" type="button" onClick={() => signOffAndLeave(upload)}>
                      Continue
                    </button>
                  </div>
                </>
              )}
              {upload.stage === 'failed' && (
                <>
                  <div className="close-message">
                    <p>
                      {"Couldn't upload — saved on this till"}
                      <br />
                      <br />
                      {upload.message}
                    </p>
                  </div>
                  <div className="pos-servers-overview__actions">
                    {upload.sessionId !== undefined && (
                      <button className="pos-servers-overview__action" type="button" onClick={retry}>
                        Retry
                      </button>
                    )}
                    <button className="pos-servers-overview__action" type="button" onClick={() => signOffAndLeave(upload)}>
                      Continue
                    </button>
                  </div>
                </>
              )}
              {upload.stage === 'idle' && notAllowed == true && (
                <>
                  <p className="pos-servers-overview__error">TRANSACTION IN PROGRESS</p>
                  <div className="pos-servers-overview__actions">
                    <button className="pos-servers-overview__action" type="button" onClick={() => navigate('../')}>
                      Exit
                    </button>
                  </div>
                </>
              )}
              {upload.stage === 'idle' && notAllowed == false && (
                <>
                  <div className="close-message">
                    <p>
                      Please Confirm you want to close the current session. <br />
                      <br />
                      {useWasteMode && workstationSession?.hasWasteTransaction === false && (
                        <span>If you have waste to log please click on Waste first.</span>
                      )}
                      {useWasteMode && workstationSession?.hasWasteTransaction === true && <span>Waste has already been completed</span>}
                    </p>
                  </div>
                  <div className="pos-servers-overview__actions">
                    {useWasteMode && workstationSession?.hasWasteTransaction === false && (
                      <button className="pos-servers-overview__action server-overview-action-waste" type="button" onClick={waste}>
                        Waste
                      </button>
                    )}
                    <button className="pos-servers-overview__action" type="button" onClick={close}>
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default CloseSession;
